import React, { useEffect, useState } from "react";
import { PageHeader, Card, Tag } from "../components/UI";
import DataTable from "../components/DataTable";
import { ShieldAlert, Check, X, Plus } from "lucide-react";
import api, { formatError } from "../lib/api";

export default function Referrals() {
  const [items, setItems] = useState([]);
  const [physios, setPhysios] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ referrer_physio_id: "", referred_mobile: "", referred_name: "", city: "" });

  async function fetchAll() {
    const [a, b] = await Promise.all([api.get("/referrals"), api.get("/physios?limit=200")]);
    setItems(a.data); setPhysios(b.data);
  }
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [a, b] = await Promise.all([api.get("/referrals"), api.get("/physios?limit=200")]);
        if (alive) { setItems(a.data); setPhysios(b.data); }
      } catch { /* swallow */ }
    })();
    return () => { alive = false; };
  }, []);

  async function submit(e) {
    e.preventDefault();
    try { await api.post("/referrals", form); setOpen(false); await fetchAll(); }
    catch (e) { alert(formatError(e)); }
  }

  async function act(id, action, e) {
    e?.stopPropagation();
    try { await api.patch(`/referrals/${id}/${action}`); await fetchAll(); }
    catch (e) { alert(formatError(e)); }
  }

  const cities = [...new Set(items.map((r) => r.city).filter(Boolean))].sort();

  const stats = {
    total: items.length,
    qualified: items.filter((r) => r.status === "qualified").length,
    rejected: items.filter((r) => r.status === "rejected").length,
    flagged: items.filter((r) => (r.fraud_signals || []).length > 0).length,
  };

  const columns = [
    { key: "referrer_name", label: "Referrer", filter: "text" },
    { key: "referred_name", label: "Referred", filter: "text" },
    { key: "referred_mobile", label: "Mobile", filter: "text", render: (r) => <span className="font-mono text-[12px]">{r.referred_mobile}</span> },
    { key: "city", label: "City", filter: "select", options: cities },
    { key: "fraud_score", label: "Fraud score", filter: "range", align: "right",
      render: (r) => (<div className="flex items-center gap-1.5 justify-end">{(r.fraud_signals || []).length === 0 ? <Tag tone="emerald">clean</Tag> : (r.fraud_signals || []).map((f) => <Tag key={f} tone="amber"><ShieldAlert size={11} /> {f}</Tag>)}<span className="text-[11px] text-slate-500">{r.fraud_score}</span></div>) },
    { key: "status", label: "Status", filter: "select", options: ["pending", "qualified", "rejected"],
      render: (r) => <Tag tone={r.status === "qualified" ? "emerald" : r.status === "rejected" ? "rose" : "amber"}>{r.status}</Tag> },
    { key: "created_at", label: "Created", filter: "text", render: (r) => new Date(r.created_at).toLocaleDateString() },
    { key: "actions", label: "", sortable: false, align: "right", render: (r) => r.status === "pending" ? (
      <div className="flex items-center gap-2 justify-end">
        <button onClick={(e) => act(r.id, "qualify", e)} className="text-emerald-700 hover:underline text-[12px] inline-flex items-center gap-1" data-testid={`qualify-${r.id}`}><Check size={12} /> Qualify</button>
        <button onClick={(e) => act(r.id, "reject", e)} className="text-rose-600 hover:underline text-[12px] inline-flex items-center gap-1"><X size={12} /> Reject</button>
      </div>
    ) : null },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Loyalty Engine"
        title="Referral Engine"
        subtitle="Advocate-driven growth with built-in fraud signals. Drill, sort and filter to find the right cohort to action."
        testId="referrals-page"
        actions={<button onClick={() => setOpen(true)} className="btn-primary flex items-center gap-2" data-testid="add-referral-btn"><Plus size={14} /> New referral</button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Total", stats.total, "slate"], ["Qualified", stats.qualified, "emerald"], ["Rejected", stats.rejected, "rose"], ["Fraud flagged", stats.flagged, "amber"]].map(([k, v, t]) => (
          <Card key={k}><div className="small-caps text-slate-500">{k}</div><div className="serif-display text-4xl numerals mt-1">{v}</div><div className="mt-2"><Tag tone={t}>live</Tag></div></Card>
        ))}
      </div>

      <Card title="All referrals">
        <DataTable columns={columns} data={items} rowKey="id" searchableKeys={["referrer_name", "referred_name", "referred_mobile", "city"]}
          exportFilename="cbc-referrals" testIdPrefix="referrals" initialSort={{ key: "created_at", dir: "desc" }} />
      </Card>

      {open && (
        <div className="fixed inset-0 bg-slate-900/30 z-40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 w-full max-w-md p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h3 className="serif-display text-2xl">New referral</h3><button type="button" onClick={() => setOpen(false)}><X size={18} /></button></div>
            <div><label className="small-caps text-slate-500">Referrer physio</label>
              <select value={form.referrer_physio_id} onChange={(e) => setForm({ ...form, referrer_physio_id: e.target.value })} required className="w-full">
                <option value="">Select…</option>
                {physios.map((p) => <option key={p.id} value={p.id}>{`${p.first_name} ${p.last_name} · ${p.erp_outlet_id || p.physio_code}`}</option>)}
              </select>
            </div>
            <div><label className="small-caps text-slate-500">Referred name</label><input value={form.referred_name} onChange={(e) => setForm({ ...form, referred_name: e.target.value })} required /></div>
            <div><label className="small-caps text-slate-500">Mobile</label><input value={form.referred_mobile} onChange={(e) => setForm({ ...form, referred_mobile: e.target.value })} required /></div>
            <div><label className="small-caps text-slate-500">City</label><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <button type="submit" className="btn-primary w-full">Create referral</button>
          </form>
        </div>
      )}
    </div>
  );
}
